/**
 * Robots > Daily Tasks — what services ran at each business on a given day.
 *
 * The data comes from the TFR Interface API (the robot backend), but this talks to
 * the portal's own server, which holds that API's credentials and performs the join.
 * Calling the robot API from here would mean shipping an admin password in the
 * bundle, so the portal proxies it instead.
 */

import { API_BASE } from '../config/apiBase';
import { APP_TIME_ZONE, formatDatePT } from '../utils/timezone';
import { authHeaders, throwApiError } from './session';

/** Statuses the robot API records against a task. "Conducted" means completed. */
export const TASK_STATUSES = [
  'completed',
  'queued',
  'in_progress',
  'failed',
  'cancelled',
  'warning',
  'unknown',
] as const;

export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Blank means every status; the server takes that as no status filter. */
export type StatusFilter = TaskStatus | '';

export interface DailyTaskRow {
  id: number;
  businessId: number;
  businessName: string;
  taskConducted: string;
  status: string;
  queuedAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationMs: number | null;
}

export interface DailyTasksReport {
  date: string;
  status: TaskStatus | null;
  rows: DailyTaskRow[];
  /** Distinct businesses with at least one task in the window. */
  businessCount: number;
  generatedAt: string;
}

export function statusLabel(status: string): string {
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/* ---------------------------------------------------------------------------
 * Shared between the day's table and a single business's page.
 *
 * Which day and filter are being viewed lives in the query string rather than in
 * component state, so a business page can be linked to directly and so returning
 * from one lands back on the day it was opened from.
 * ------------------------------------------------------------------------- */

/** What the view is looking at. */
export interface DailyTasksView {
  date: string;
  status: StatusFilter;
  /** Business the table is narrowed to, by id, or '' for all of them. */
  business: string;
}

/** Today in Pacific Time as YYYY-MM-DD — the shape a date input expects. */
export function pacificToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIME_ZONE }).format(new Date());
}

/**
 * "Fri, Aug 28, 2026" for a plain YYYY-MM-DD.
 *
 * Anchored at midday UTC because midnight on a bare date parses as UTC, which in
 * Pacific Time is still the day before and would label every view off by one.
 */
export function dayLabel(day: string): string {
  return formatDatePT(`${day}T12:00:00Z`, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * The status behind a `status` query parameter.
 *
 * Blank means every status, and that is the default: a day reads better whole, and
 * the outcome column already breaks it down per business. Anything unrecognised
 * falls back to the same default so a hand-edited URL still renders.
 */
export function parseStatusFilter(raw: string | null): StatusFilter {
  if (!raw) return '';
  return (TASK_STATUSES as readonly string[]).includes(raw) ? (raw as TaskStatus) : '';
}

/**
 * The query string carrying a view between the two pages.
 *
 * Status is always written, empty included, so the view round-trips exactly rather
 * than being re-derived from whatever the default happens to be at the time.
 */
export function dailyTasksQuery(view: DailyTasksView): URLSearchParams {
  const params = new URLSearchParams({ date: view.date, status: view.status });
  if (view.business) params.set('business', view.business);
  return params;
}

/** Where a business name in the day's table points. */
export function businessTasksPath(businessId: number, view: DailyTasksView): string {
  return `/robots/daily-tasks/${businessId}?${dailyTasksQuery(view)}`;
}

/** Back to the day's table, on the day and filter it was left on. */
export function dailyTasksPath(view: DailyTasksView): string {
  return `/robots?${dailyTasksQuery(view)}`;
}

/** Epoch ms, so times sort and compare by instant rather than by their text. */
export function instant(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** One business's day, carrying the tasks behind it for the drill-down. */
export interface BusinessSummary {
  businessId: number;
  businessName: string;
  taskCount: number;
  /** Distinct service names, alphabetical. */
  services: string[];
  /** Counts per status, busiest first. */
  statusCounts: { status: string; count: number }[];
  firstQueuedAt: string | null;
  lastQueuedAt: string | null;
  tasks: DailyTaskRow[];
}

/**
 * Collapses a day's tasks into one row per business.
 *
 * Keyed on business id rather than name: the robot API does not guarantee names are
 * unique, so two businesses sharing one would otherwise be merged into a single row.
 */
export function summarizeByBusiness(rows: readonly DailyTaskRow[]): BusinessSummary[] {
  const grouped = new Map<number, DailyTaskRow[]>();
  for (const row of rows) {
    const existing = grouped.get(row.businessId);
    if (existing) existing.push(row);
    else grouped.set(row.businessId, [row]);
  }

  return Array.from(grouped.values()).map((tasks) => {
    const counts = new Map<string, number>();
    const services = new Set<string>();
    let first: number | null = null;
    let last: number | null = null;
    let firstAt: string | null = null;
    let lastAt: string | null = null;

    for (const task of tasks) {
      counts.set(task.status, (counts.get(task.status) ?? 0) + 1);
      services.add(task.taskConducted);
      const at = instant(task.queuedAt);
      if (at == null) continue;
      if (first == null || at < first) {
        first = at;
        firstAt = task.queuedAt;
      }
      if (last == null || at > last) {
        last = at;
        lastAt = task.queuedAt;
      }
    }

    return {
      businessId: tasks[0].businessId,
      businessName: tasks[0].businessName,
      taskCount: tasks.length,
      services: Array.from(services).sort((a, b) => a.localeCompare(b)),
      statusCounts: Array.from(counts.entries())
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count || a.status.localeCompare(b.status)),
      firstQueuedAt: firstAt,
      lastQueuedAt: lastAt,
      tasks,
    };
  });
}

export async function fetchDailyTasks(
  date: string,
  status: StatusFilter,
  signal?: AbortSignal
): Promise<DailyTasksReport> {
  const query = new URLSearchParams({ date });
  // Always sent, so a blank value reads as "every status" rather than as the
  // server's default of completed.
  query.set('status', status);

  const res = await fetch(`${API_BASE}/api/robots/daily-tasks?${query}`, {
    headers: authHeaders(),
    signal,
  });
  if (!res.ok) await throwApiError(res, 'Failed to load the daily tasks report');
  return (await res.json()) as DailyTasksReport;
}
